import type {
  MembershipSeatAuthorityReport,
} from './MembershipSeatAuthority';

export type MembershipInviteType =
  | 'FAMILY_PROFILE'
  | 'SPONSORED_SEAT';

export type MembershipInviteDeliveryChannel =
  | 'email'
  | 'sms'
  | 'link'
  | 'share'
  | 'contacts';

export type MembershipInviteLifecycleStatus =
  | 'SENT'
  | 'OPENED'
  | 'RESERVED'
  | 'CLAIMED'
  | 'ACCEPTED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'RETURNED'
  | 'FAILED';

export interface MembershipInviteCreationInput {
  inviteType: MembershipInviteType;
  deliveryChannel: MembershipInviteDeliveryChannel;
  seatAuthority: MembershipSeatAuthorityReport;
  createdAt?: string;
}

export interface MembershipInviteCreationDecision {
  allowed: boolean;
  inviteType: MembershipInviteType;
  initialStatus: 'SENT' | 'RESERVED';
  reservesSeat: boolean;
  inviteExpiresAt: string;
  seatReservationExpiresAt: string | null;
  reason:
    | 'FAMILY_PROFILE_INVITE_ALLOWED'
    | 'SPONSORED_SEAT_RESERVED'
    | 'SPONSORED_CAPACITY_FULL'
    | 'INVALID_SEAT_AUTHORITY';
}

const FAMILY_INVITE_EXPIRATION_DAYS = 30;
const SPONSORED_RESERVATION_DAYS = 7;

function addDays(isoDate: string, days: number): string {
  const value = new Date(isoDate);

  if (Number.isNaN(value.getTime())) {
    throw new Error('Invalid invite creation date.');
  }

  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString();
}

export function decideInviteCreation(
  input: MembershipInviteCreationInput,
): MembershipInviteCreationDecision {
  const createdAt =
    input.createdAt ?? new Date().toISOString();

  const inviteExpiresAt = addDays(
    createdAt,
    FAMILY_INVITE_EXPIRATION_DAYS,
  );

  if (input.seatAuthority.status === 'INVALID') {
    return Object.freeze({
      allowed: false,
      inviteType: input.inviteType,
      initialStatus: 'SENT',
      reservesSeat: false,
      inviteExpiresAt,
      seatReservationExpiresAt: null,
      reason: 'INVALID_SEAT_AUTHORITY',
    });
  }

  if (input.inviteType === 'FAMILY_PROFILE') {
    return Object.freeze({
      allowed: true,
      inviteType: input.inviteType,
      initialStatus: 'SENT',
      reservesSeat: false,
      inviteExpiresAt,
      seatReservationExpiresAt: null,
      reason: 'FAMILY_PROFILE_INVITE_ALLOWED',
    });
  }

  if (!input.seatAuthority.canReserveSponsoredSeat) {
    return Object.freeze({
      allowed: false,
      inviteType: input.inviteType,
      initialStatus: 'SENT',
      reservesSeat: false,
      inviteExpiresAt,
      seatReservationExpiresAt: null,
      reason: 'SPONSORED_CAPACITY_FULL',
    });
  }

  return Object.freeze({
    allowed: true,
    inviteType: input.inviteType,
    initialStatus: 'RESERVED',
    reservesSeat: true,
    inviteExpiresAt,
    seatReservationExpiresAt: addDays(
      createdAt,
      SPONSORED_RESERVATION_DAYS,
    ),
    reason: 'SPONSORED_SEAT_RESERVED',
  });
}

export function doesInviteReturnSeat(
  inviteType: MembershipInviteType,
  status: MembershipInviteLifecycleStatus,
): boolean {
  if (inviteType !== 'SPONSORED_SEAT') {
    return false;
  }

  return [
    'EXPIRED',
    'CANCELLED',
    'REJECTED',
    'RETURNED',
  ].includes(status);
}

export const MEMBERSHIP_INVITE_LIFECYCLE_READY = true;
